<template>
  <v-card class="filter-panel pa-4" width="300">
    <v-select v-model="local.state" :items="states" :label="$t('board.state_label')" multiple clearable
      hide-details dense prepend-inner-icon="mdi-filter-variant" class="mb-4" @change="emitNow" />

    <v-autocomplete v-model="local.car_id" :items="cars" item-value="id" :item-text="carLabel"
      :label="$t('board.vehicle')" clearable hide-details dense prepend-inner-icon="mdi-car" class="mb-4"
      :loading="carsLoading" @focus="$emit('load-cars')" @change="emitNow" />

    <v-select v-model="local.responsible" :items="responsibles" item-value="name" item-text="name"
      :label="$t('board.responsible')" clearable hide-details dense prepend-inner-icon="mdi-account" class="mb-4"
      :loading="responsiblesLoading" @focus="$emit('load-responsibles')" @change="emitNow" />

    <v-select v-model="local.payment_status" :items="paymentStatusItems" :label="$t('board.payment_status')"
      clearable hide-details dense prepend-inner-icon="mdi-cash-multiple" class="mb-4" @change="emitNow" />

    <v-checkbox v-model="local.overdue" :label="$t('board.overdue_label')" hide-details dense
      class="mt-0 mb-4" @change="emitNow" />

    <v-checkbox v-if="showDeposit" v-model="local.deposit_return" :label="$t('board.deposit_return_label')"
      hide-details dense class="mt-0 mb-4" @change="emitNow" />

    <div class="d-flex flex-wrap mb-4" style="gap: 6px">
      <v-chip small label outlined @click="setPreset('today')">{{ $t("custom.preset_today") }}</v-chip>
      <v-chip small label outlined @click="setPreset('tomorrow')">{{ $t("custom.preset_tomorrow") }}</v-chip>
      <v-chip small label outlined @click="setPreset('week')">{{ $t("custom.preset_week") }}</v-chip>
    </div>
    <v-menu v-model="menuFrom" :close-on-content-click="false" min-width="auto">
      <template v-slot:activator="{ on }">
        <v-text-field :value="fmtDate(local.date_from)" :label="$t('board.date_from')" prepend-inner-icon="mdi-calendar"
          readonly clearable hide-details dense class="mb-4" v-on="on" @click:clear="clearFrom" />
      </template>
      <v-date-picker v-model="local.date_from" :locale="$i18n.locale" first-day-of-week="1"
        @input="menuFrom = false; emitNow()" />
    </v-menu>
    <v-menu v-model="menuTo" :close-on-content-click="false" min-width="auto">
      <template v-slot:activator="{ on }">
        <v-text-field :value="fmtDate(local.date_to)" :label="$t('board.date_to')" prepend-inner-icon="mdi-calendar"
          readonly clearable hide-details dense v-on="on" @click:clear="clearTo" />
      </template>
      <v-date-picker v-model="local.date_to" :locale="$i18n.locale" first-day-of-week="1"
        @input="menuTo = false; emitNow()" />
    </v-menu>

    <template v-if="showSort">
      <v-divider class="my-4"></v-divider>
      <div class="d-flex align-center" style="gap: 8px">
        <v-select v-model="local.sort_by" :items="sortItems" :label="$t('board.sort_by')" hide-details dense
          prepend-inner-icon="mdi-sort" @change="emitNow" />
        <v-btn icon small :title="$t('board.sort_by')" @click="toggleDir">
          <v-icon>{{ local.sort_dir === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending' }}</v-icon>
        </v-btn>
      </div>
    </template>

    <!-- FT-082: saved filter presets (localStorage) -->
    <v-divider class="my-4"></v-divider>
    <div v-if="presets.length" class="d-flex flex-wrap align-center mb-3" style="gap: 6px">
      <v-icon small class="mr-1">mdi-bookmark-outline</v-icon>
      <v-chip v-for="(p, i) in presets" :key="i" small close @click="applyPreset(p)" @click:close="deletePreset(i)">
        {{ p.name }}
      </v-chip>
    </div>
    <div class="d-flex align-center" style="gap: 8px">
      <v-text-field v-model="presetName" :label="$t('cars.name')" hide-details dense
        @keyup.enter="savePreset" />
      <v-btn small color="primary" :disabled="!presetName.trim()" @click="savePreset">
        <v-icon left small>mdi-content-save-outline</v-icon>{{ $t("custom.save") }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import moment from "moment";

export default {
  name: "BookingsFilterPanel",
  props: {
    value: { type: Object, required: true },
    cars: { type: Array, default: () => [] },
    responsibles: { type: Array, default: () => [] },
    states: { type: Array, default: () => [] },
    showSort: { type: Boolean, default: false },
    showDeposit: { type: Boolean, default: false },
    carsLoading: { type: Boolean, default: false },
    responsiblesLoading: { type: Boolean, default: false },
  },
  data() {
    return { local: { ...this.value }, menuFrom: false, menuTo: false, presets: [], presetName: "" };
  },
  created() {
    this.presets = this.loadPresets();
  },
  computed: {
    sortItems() {
      return [
        { text: this.$t("custom.created"), value: "created_at" },
        { text: this.$t("custom.start"), value: "start_date" },
        { text: this.$t("custom.end"), value: "end_date" },
        { text: this.$t("custom.days"), value: "days" },
        { text: this.$t("custom.number"), value: "number" },
      ];
    },
    paymentStatusItems() {
      return [
        { text: this.$t("board.pay_underpaid"), value: "underpaid" },
        { text: this.$t("board.pay_paid"), value: "paid" },
        { text: this.$t("board.pay_overpaid"), value: "overpaid" },
      ];
    },
  },
  watch: {
    value(v) { this.local = { ...v }; },
  },
  methods: {
    carLabel(c) { return `${c.car_name || ""}(${c.code || ""})`; },
    emitNow() { this.$emit("input", { ...this.local }); },
    toggleDir() {
      this.local.sort_dir = this.local.sort_dir === "asc" ? "desc" : "asc";
      this.emitNow();
    },
    clearFrom() { this.local.date_from = null; this.emitNow(); },
    clearTo() { this.local.date_to = null; this.emitNow(); },
    // display DD.MM.YYYY while the model stays ISO (picker + backend want ISO)
    fmtDate(d) { return d ? moment(d).format("DD.MM.YYYY") : ""; },
    // FT-082: quick date presets (filter on start_date — "who picks up ...").
    setPreset(kind) {
      const fmt = "YYYY-MM-DD";
      if (kind === "today") {
        const d = moment().format(fmt);
        this.local.date_from = d;
        this.local.date_to = d;
      } else if (kind === "tomorrow") {
        const d = moment().add(1, "day").format(fmt);
        this.local.date_from = d;
        this.local.date_to = d;
      } else if (kind === "week") {
        this.local.date_from = moment().format(fmt);
        this.local.date_to = moment().add(6, "days").format(fmt);
      }
      this.emitNow();
    },
    // FT-082: saved filter presets in localStorage (per browser).
    loadPresets() {
      try {
        const arr = JSON.parse(localStorage.getItem("bookings_filter_presets") || "[]");
        if (!Array.isArray(arr)) return [];
        // drop malformed entries so a stale/garbage value can't crash the render
        return arr.filter((p) => p && typeof p.name === "string" && p.filters);
      } catch (e) {
        return [];
      }
    },
    persistPresets() {
      try {
        localStorage.setItem("bookings_filter_presets", JSON.stringify(this.presets));
      } catch (e) {
        // storage unavailable/full — keep the in-memory list, ignore
      }
    },
    savePreset() {
      const name = this.presetName.trim();
      if (!name) return;
      const f = this.local;
      const preset = {
        name,
        filters: {
          search: f.search || null,
          state: f.state || [],
          car_id: f.car_id || null,
          responsible: f.responsible || null,
          date_from: f.date_from || null,
          date_to: f.date_to || null,
          payment_status: f.payment_status || null,
          overdue: f.overdue || false,
          deposit_return: f.deposit_return || false,
          sort_by: f.sort_by,
          sort_dir: f.sort_dir,
        },
      };
      const idx = this.presets.findIndex((p) => p.name === name);
      if (idx >= 0) this.presets.splice(idx, 1, preset);
      else this.presets.push(preset);
      this.persistPresets();
      this.presetName = "";
    },
    applyPreset(p) {
      const f = p.filters || {};
      this.local = {
        ...this.local,
        search: f.search || null,
        state: f.state || [],
        car_id: f.car_id || null,
        responsible: f.responsible || null,
        date_from: f.date_from || null,
        date_to: f.date_to || null,
        payment_status: f.payment_status || null,
        overdue: f.overdue || false,
        deposit_return: f.deposit_return || false,
        ...(f.sort_by ? { sort_by: f.sort_by, sort_dir: f.sort_dir } : {}),
      };
      this.emitNow();
    },
    deletePreset(i) {
      this.presets.splice(i, 1);
      this.persistPresets();
    },
  },
};
</script>
